import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Loader2, CheckCircle, Smartphone, ExternalLink } from 'lucide-react';
import {
  attemptAppRedirect,
  getAppConfig,
  getSuccessMessage,
  type AppReference,
} from '@/utils/deep-link';
import { toast } from 'sonner';
import { userOnboardingCompletionStore } from '@/stores/user-onboarding-completion';

function OnboardingSuccessPage() {
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [redirectAttempted, setRedirectAttempted] = useState(false);
  const [countdown, setCountdown] = useState(2);

  const search = Route.useSearch();
  const ref = (search.ref as AppReference) || 'webapp';
  const isCleanerWeb = search.ref === 'cleanerweb';

  const config = getAppConfig(ref);
  const successMessage = getSuccessMessage(ref);
  const isWebApp = ref === 'webapp' || isCleanerWeb;

  const setCurrentStep = userOnboardingCompletionStore(s => s.setCurrentStep);

  useEffect(() => {
    // Special handling for cleaner web ref
    if (isCleanerWeb && !redirectAttempted) {
      setCurrentStep(5);
      setIsRedirecting(true);

      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      const redirectTimer = setTimeout(() => {
        clearInterval(timer);
        setCountdown(0);
        setIsRedirecting(false);
        setRedirectAttempted(true);
        navigate({ to: '/onboarding' });
      }, 2000);

      return () => {
        clearInterval(timer);
        clearTimeout(redirectTimer);
      };
    }

    if (!redirectAttempted && !isWebApp) {
      setIsRedirecting(true);

      // Countdown timer
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Attempt redirect after countdown
      const redirectTimer = setTimeout(async () => {
        clearInterval(timer);
        setCountdown(0);

        try {
          const appOpened = await attemptAppRedirect(ref);

          if (!appOpened) {
            // App didn't open, show fallback button
            toast.success('Redirecting to app store...');
          }
        } catch {
          toast.error('Failed to open app. Please use the button below.');
        } finally {
          setIsRedirecting(false);
          setRedirectAttempted(true);
        }
      }, 2000);

      return () => {
        clearInterval(timer);
        clearTimeout(redirectTimer);
      };
    } else if (isWebApp && !redirectAttempted) {
      // For webapp, redirect to homepage after 2 seconds
      const timer = setTimeout(() => {
        navigate({ to: '/' });
      }, 2000);

      setRedirectAttempted(true);

      return () => clearTimeout(timer);
    }
  }, [
    isCleanerWeb,
    isWebApp,
    navigate,
    redirectAttempted,
    ref,
    setCurrentStep,
  ]);

  const handleManualRedirect = async () => {
    if (isWebApp) {
      navigate({ to: '/' });
      return;
    }

    setIsRedirecting(true);
    try {
      await attemptAppRedirect(ref);
    } catch {
      toast.error('Failed to open app. Please check your app store.');
    } finally {
      setIsRedirecting(false);
    }
  };

  const handleStoreRedirect = () => {
    if (!config) return;

    const deviceType = /iPad|iPhone|iPod/.test(navigator.userAgent)
      ? 'ios'
      : /Android/.test(navigator.userAgent)
        ? 'android'
        : 'desktop';

    const storeUrl =
      deviceType === 'ios' ? config.iOSStoreUrl : config.AndroidStoreUrl;
    window.open(storeUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-4">
            <CheckCircle className="w-16 h-16 text-green-500" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {successMessage.title}
          </CardTitle>
          <CardDescription className="text-base">
            {successMessage.message}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4 pt-0">
          {isRedirecting && countdown > 0 && !isWebApp && (
            <div className="text-center space-y-2">
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Redirecting in {countdown}...</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-1000"
                  style={{ width: `${(countdown / 2) * 100}%` }}
                />
              </div>
            </div>
          )}

          {isWebApp && countdown > 0 && (
            <div className="text-center space-y-2">
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Redirecting in {countdown}...</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-1000"
                  style={{ width: `${(countdown / 2) * 100}%` }}
                />
              </div>
            </div>
          )}

          {!isRedirecting && (
            <div className="space-y-3">
              <Button
                onClick={handleManualRedirect}
                className="w-full"
                size="lg"
              >
                {isWebApp ? (
                  <>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {successMessage.buttonText}
                  </>
                ) : (
                  <>
                    <Smartphone className="w-4 h-4 mr-2" />
                    {successMessage.buttonText}
                  </>
                )}
              </Button>

              {!isWebApp && config && (
                <Button
                  onClick={handleStoreRedirect}
                  variant="outline"
                  className="w-full"
                >
                  Download from App Store
                </Button>
              )}
            </div>
          )}

          <div className="text-center pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {config?.name || 'Web App'}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/onboarding-success')({
  component: OnboardingSuccessPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      ref: (search.ref as string) || 'webapp',
    };
  },
});
